import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationProgramHeroForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function AdmissionsHeroForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  // Form values (auto-filled from content_published)
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    slides: []  // Array of objects: { image: mediaId }
  });

  // Pending approvals per field (title, subtitle)
  const [pendingFields, setPendingFields] = useState({
    title: false,
    subtitle: false
  });

  // Pending approvals per slide (slide_0, slide_1, slide_2)
  const [pendingSlides, setPendingSlides] = useState({});

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({...formData});

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingSlideIndex, setEditingSlideIndex] = useState(null);
  const [modalSlideData, setModalSlideData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    // Load media map and section data in parallel
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      console.log('[AdmissionsHeroForm] Fetching media map...');
      const response = await fetch(
        `${apiBase}/admissions/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) {
        console.warn('[AdmissionsHeroForm] Media fetch failed:', response.status);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          // Handle both numeric and string IDs
          const mediaId = media.id || media.media_id;
          // Get URL from multiple possible sources
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          
          if (mediaId && mediaUrl) {
            // Store both as string and number to handle any type conversion
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            map[parseInt(mediaId)] = mediaUrl;
            console.log(`[AdmissionsHeroForm] ✓ Media ${mediaId} → ${mediaUrl.substring(0, 60)}...`);
          }
        });
        
        console.log('[AdmissionsHeroForm] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[AdmissionsHeroForm] Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[AdmissionsHeroForm] Loading admissions hero section...');
      
      // Try two approaches: first with admin auth, then fallback to public
      let heroSection = null;
      
      // Approach 1: Try admin endpoint (requires auth)
      console.log('[AdmissionsHeroForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/admissions/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        
        if (result.ok && Array.isArray(result.data)) {
          heroSection = result.data.find(s => s.section_type === 'admissions-hero');
          console.log('[AdmissionsHeroForm] ✓ Got data from admin endpoint');
        }
      } else if (adminResponse.status === 401) {
        console.log('[AdmissionsHeroForm] Admin endpoint unauthorized, trying public endpoint...');
      } else {
        console.warn('[AdmissionsHeroForm] Admin endpoint returned:', adminResponse.status);
      }
      
      // Approach 2: Fallback to public endpoint
      if (!heroSection) {
        const publicResponse = await fetch(
          `${apiBase}/admissions/cms/sections?published=true`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          
          if (result.ok && Array.isArray(result.data)) {
            heroSection = result.data.find(s => s.section_type === 'admissions-hero');
            console.log('[AdmissionsHeroForm] ✓ Got data from public endpoint');
          }
        } else {
          throw new Error(`Public endpoint returned ${publicResponse.status}`);
        }
      }
      
      if (!heroSection) {
        throw new Error('Hero section not found');
      }

      const sectionId = heroSection.id;
      setSectionId(sectionId);

      console.log('[AdmissionsHeroForm] Found hero section:', sectionId);

      // Load section with pending changes (if authenticated)
      let pendingFieldsMap = { title: false, subtitle: false };
      let pendingSlidesMap = {};
      
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${sectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        try {
          const pendingResult = await pendingResponse.json();
          
          if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
            const { pending_changes } = pendingResult.data;

            if (Array.isArray(pending_changes)) {
              pending_changes.forEach(change => {
                if (change.status === 'pending' && change.locale === 'en') {
                  if (change.field_id === 'title' || change.field_id === 'subtitle') {
                    pendingFieldsMap[change.field_id] = true;
                  } else if (change.field_id && change.field_id.includes('slides.')) {
                    const match = change.field_id.match(/slides\.(\d+)\./);
                    if (match) {
                      const slideIdx = parseInt(match[1]);
                      pendingSlidesMap[`slide_${slideIdx}`] = true;
                    }
                  }
                }
              });
            }
          }
        } catch (e) {
          console.warn('[AdmissionsHeroForm] Error parsing pending changes:', e);
        }
      }

      // Extract published content
      if (heroSection.content_published) {
        let published = heroSection.content_published;
        
        if (typeof published === 'string') {
          try {
            published = JSON.parse(published);
          } catch (e) {
            published = {};
          }
        }

        const publishedEn = published.en || {};
        
        // Transform slides: convert old format { slide: id } to new format { image: id }
        const transformedSlides = Array.isArray(publishedEn.slides) 
          ? publishedEn.slides.map((slideData, idx) => {
              // Always ensure we return proper object format { image: id, order: n }
              if (typeof slideData === 'object' && slideData !== null) {
                // Handle both old format { slide: id } and new format { image: id }
                const imageId = slideData.image !== undefined ? slideData.image : slideData.slide;
                return {
                  image: imageId || '',
                  order: slideData.order || idx + 1
                };
              } else if (slideData) {
                // Raw ID or fallback
                return {
                  image: String(slideData).trim(),
                  order: idx + 1
                };
              } else {
                // Empty slide
                return {
                  image: '',
                  order: idx + 1
                };
              }
            })
          : [];
        
        const initialData = {
          title: publishedEn.title || '',
          subtitle: publishedEn.subtitle || '',
          slides: transformedSlides
        };

        console.log(`[AdmissionsHeroForm] Initial data loaded:`, {
          title: initialData.title,
          subtitle: initialData.subtitle?.substring(0, 80),
          slidesCount: initialData.slides.length
        });

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingFieldsMap);
        setPendingSlides(pendingSlidesMap);

        console.log('[AdmissionsHeroForm] ✓ Section loaded:', {
          id: sectionId,
          slidesCount: initialData.slides.length,
          pendingFields: pendingFieldsMap,
          pendingSlides: pendingSlidesMap
        });
      }
    } catch (err) {
      console.error('[AdmissionsHeroForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSlideChange = (index, field, value) => {
    setFormData(prev => {
      const newSlides = [...prev.slides];
      // Ensure the slide is always an object, not a string or primitive
      if (typeof newSlides[index] !== 'object' || newSlides[index] === null) {
        newSlides[index] = { image: '', order: index + 1 };
      }
      newSlides[index] = {
        ...newSlides[index],
        [field]: value
      };
      return {
        ...prev,
        slides: newSlides
      };
    });
  };

  const handleAddSlide = () => {
    if (formData.slides.length < 3) {
      setFormData(prev => ({
        ...prev,
        slides: [...prev.slides, { image: '', order: prev.slides.length + 1 }]
      }));
    }
  };

  const handleDeleteSlide = (index) => {
    if (window.confirm('Delete this slide?')) {
      setFormData(prev => ({
        ...prev,
        slides: prev.slides.filter((_, i) => i !== index)
      }));
      // Remove pending status for this slide
      setPendingSlides(prev => {
        const updated = {...prev};
        delete updated[`slide_${index}`];
        return updated;
      });
    }
  };

  const handleImageUpload = async (slideIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    setImageUploading(prev => ({ ...prev, [slideIndex]: true }));
    setImageProgress(prev => ({ ...prev, [slideIndex]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          console.log('[AdmissionsHeroForm] Upload progress:', percent + '%');
          setImageProgress(prev => ({ ...prev, [slideIndex]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[AdmissionsHeroForm] Upload completed, status:', xhr.status);
        console.log('[AdmissionsHeroForm] Response text:', xhr.responseText);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('[AdmissionsHeroForm] Parsed response:', response);
            
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id || response.filename;
              console.log('[AdmissionsHeroForm] ✓ Image uploaded, media ID:', mediaId);
              
              if (mediaId) {
                // Update only the image field of the slide object
                handleSlideChange(slideIndex, 'image', mediaId);
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            console.error('[AdmissionsHeroForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          console.error('[AdmissionsHeroForm] Upload failed with status', xhr.status);
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [slideIndex]: false }));
      });

      xhr.addEventListener('error', () => {
        console.error('[AdmissionsHeroForm] XHR error event');
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [slideIndex]: false }));
      });

      console.log('[AdmissionsHeroForm] Opening XHR to:', `${apiBase}/admissions/cms/media/upload?section_type=admissions-hero`);
      xhr.open('POST', `${apiBase}/admissions/cms/media/upload?section_type=admissions-hero`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[AdmissionsHeroForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [slideIndex]: false }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    console.log(`[AdmissionsHeroForm] Submitting field: ${fieldName}`, {
      old: originalData[fieldName],
      new: formData[fieldName],
      sectionId
    });

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const requestBody = {
        section_id: sectionId,
        field_id: fieldName,
        field_name: fieldName.replace(/_/g, ' '),
        new_value: formData[fieldName],
        old_value: originalData[fieldName],
        locale: 'en'
      };
      
      console.log(`[AdmissionsHeroForm] Sending request:`, JSON.stringify(requestBody));

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      const result = await response.json();
      console.log(`[AdmissionsHeroForm] Response from submit:`, result);

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        // Mark as pending locally
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        // Update original data
        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitSlide = async (slideIndex) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const slide = formData.slides[slideIndex];
    if (!slide || !slide.image) {
      toast.warning('Please upload an image first');
      return;
    }

    const slideKey = `slide_${slideIndex}`;
    setSubmitting(prev => ({ ...prev, [slideKey]: true }));

    try {
      console.log('[AdmissionsHeroForm] Submitting slide', slideIndex, slide);

      // Use slides.X.update format for slide operations
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: `slides.${slideIndex}.update`,
            field_name: `Update Slide ${slideIndex + 1}`,
            old_value: originalData.slides[slideIndex] || { image: '' },
            new_value: slide,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      console.log('[AdmissionsHeroForm] Submit response:', result);

      if (result.success) {
        toast.success(`Slide ${slideIndex + 1} submitted for approval`);
        
        // Mark this slide as pending
        setPendingSlides(prev => ({
          ...prev,
          [slideKey]: true
        }));

        // Update original data
        setOriginalData(prev => ({
          ...prev,
          slides: prev.slides.map((s, i) => i === slideIndex ? slide : s)
        }));
      } else {
        if (response.status === 409) {
          toast.error(`Slide ${slideIndex + 1} already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[AdmissionsHeroForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [slideKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-hero-form-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading Admissions Hero Section...
        </div>
      </div>
    );
  }

  return (
    <div className="education-hero-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">Admissions Hero Section</h2>
          <p className="hero-form-description">Manage hero title, subtitle, and carousel slides</p>
        </div>

        {/* TITLE SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Hero Title</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Title *</label>
              {pendingFields.title && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              maxLength="100"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={submitting.title || pendingFields.title}
              placeholder="e.g., Admissions"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              className="submit-btn"
            >
              {submitting.title ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* SUBTITLE SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Hero Subtitle</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Subtitle *</label>
              {pendingFields.subtitle && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <textarea
              maxLength="500"
              value={formData.subtitle}
              onChange={(e) => handleFieldChange('subtitle', e.target.value)}
              disabled={submitting.subtitle || pendingFields.subtitle}
              placeholder="Brief hero description..."
              className="textarea-input"
              rows="4"
            />
            <button
              onClick={() => handleSubmitField('subtitle')}
              disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
              className="submit-btn"
            >
              {submitting.subtitle ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* SLIDES SECTION */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3 className="form-section-title">Hero Slides (Max 3)</h3>
            {formData.slides.length < 3 && (
              <button type="button" onClick={handleAddSlide} className="add-item-btn">+ Add Slide</button>
            )}
          </div>

          {formData.slides.length === 0 ? (
            <div className="items-empty-state">
              <div className="empty-icon">🖼️</div>
              <h4>No slides yet</h4>
              <p>Click "Add Slide" to create carousel slides</p>
            </div>
          ) : (
            <div className="cards-grid">
              {formData.slides.map((slide, idx) => {
                let safeSlide = '';
                if (typeof slide === 'object' && slide !== null) {
                  safeSlide = slide.image || slide.slide || '';
                } else if (typeof slide === 'string' || typeof slide === 'number') {
                  safeSlide = String(slide);
                }
                
                return (
                  <div
                    key={idx}
                    className="slide-card"
                    onClick={() => {
                      setEditingSlideIndex(idx);
                      setModalSlideData({...slide});
                    }}
                  >
                    {safeSlide && mediaMap[safeSlide] && (
                      <div className="card-image">
                        <img src={mediaMap[safeSlide]} alt={`Slide ${idx + 1}`} />
                      </div>
                    )}
                    <div className="card-content">
                      <div className="card-order">Slide {idx + 1}</div>
                      <h4 className="card-title">Carousel Slide</h4>
                      {!safeSlide && <p className="card-desc">Click to upload image</p>}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSlide(idx);
                      }}
                      className="card-delete"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* EDIT SLIDE MODAL */}
        {editingSlideIndex !== null && modalSlideData && (
          <div className="modal-overlay" onClick={() => {
            setEditingSlideIndex(null);
            setModalSlideData(null);
          }}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Slide {editingSlideIndex + 1}</h3>
                <button className="modal-close" onClick={() => {
                  setEditingSlideIndex(null);
                  setModalSlideData(null);
                }}>×</button>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <label>Slide Image</label>
                  {modalSlideData.image && mediaMap[modalSlideData.image] && (
                    <div className="modal-image-preview">
                      <img src={mediaMap[modalSlideData.image]} alt="Slide preview" />
                    </div>
                  )}
                  <div className="image-upload-area">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => handleImageUpload(editingSlideIndex, e)}
                      disabled={imageUploading[editingSlideIndex]}
                      className="image-input"
                      id="modal-slide-image-input"
                    />
                    <label htmlFor="modal-slide-image-input" className={`upload-label ${imageUploading[editingSlideIndex] ? 'uploading' : ''}`}>
                      {imageUploading[editingSlideIndex] ? `Uploading ${imageProgress[editingSlideIndex]}%...` : 'Upload Image'}
                    </label>
                  </div>
                  <span className="hint">Max 5MB • Recommended: 1600×900px • JPG, PNG, WebP</span>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => {
                  setEditingSlideIndex(null);
                  setModalSlideData(null);
                }}>Cancel</button>
                <button
                  className="btn-submit"
                  disabled={submitting[`slide_${editingSlideIndex}`] || !modalSlideData.image}
                  onClick={() => handleSubmitSlide(editingSlideIndex)}
                >
                  {submitting[`slide_${editingSlideIndex}`] ? 'Submitting...' : 'Save & Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
